import Slider from '@react-native-community/slider';
import { LinearGradient } from 'expo-linear-gradient';
import * as StoreReview from 'expo-store-review';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { useCreateFeedbackMutation } from 'services/backend';
import { RootState } from 'store/index';
import {
  checkShouldBeOpen,
  setAvoidRatingModal,
  setFiveDaysFromNow,
  setForceToBeClose,
  setFutureRatingModal,
} from 'store/slices/ratingSlice';

import BandHeaderLogo from 'assets/favicon.png';

import { Icons } from 'components/Icons';
import DisLikeIcon from 'components/Icons/DisLike';
import LikeIcon from 'components/Icons/Like';
import { BaseModal } from 'components/Modal';

const TOPIC_OPTIONS = [
  'Aplicativo está lento',
  'Aplicativo trava',
  'Aplicativo fecha inesperadamente',
  'Dificuldades na navegação',
  'Catálogo não me agradou',
  'Outro problema. Qual?',
];

export function AssessmentModal() {
  const analytics = useAnalytics();
  const user = useSelector((state: RootState) => state.auth);
  const rating = useSelector((state: RootState) => state.rating);
  const { hasCompletedNavigationTour } = useSelector((state: RootState) => state.userDeviceData);

  const [currentStep, setCurrentStep] = useState(0);

  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [_message, _setMessage] = useState('');
  const [sliderValue, setSliderValue] = useState(1);

  const [createFeedback] = useCreateFeedbackMutation();

  const dispatch = useDispatch();

  useEffect(() => {
    user.isAuthenticated && hasCompletedNavigationTour && dispatch(checkShouldBeOpen());
  }, []);

  useEffect(() => {
    if (rating.shouldItOpen) analytics.triggerReviewPageviewEvent();
  }, [rating]);

  const handleFormSubmit = () => {
    const formData = {
      message: _message,
      rating: sliderValue,
      topics: selectedTopics,
    };

    createFeedback(formData);
    setCurrentStep(2);
  };

  async function openStore() {
    analytics.triggerReviewLikeActionEvent();
    dispatch(setAvoidRatingModal({ suppressRatingModal: true }));
    createFeedback({ topics: [], message: '(feedback positivo)', rating: 5 });
    dispatch(setForceToBeClose());

    setTimeout(async () => {
      if (await StoreReview.hasAction()) {
        StoreReview.requestReview().catch((error) => console.log(error));
      }
    }, 2000);
  }

  function handleFinishForm() {
    setCurrentStep(0);
    dispatch(setFutureRatingModal({ quantityOfFutureDay: 30 }));
    dispatch(setForceToBeClose());
  }

  function handleCloseModal() {
    setCurrentStep(0);
    dispatch(setFiveDaysFromNow({ fiveDaysFromNow: null }));
    dispatch(setForceToBeClose());
  }

  function handleAvoidModal() {
    setCurrentStep(0);
    dispatch(setFutureRatingModal({ quantityOfFutureDay: 15 }));
    dispatch(setForceToBeClose());
  }

  function dislikeApp() {
    analytics.triggerReviewDislikeActionEvent();
    setCurrentStep(1);
  }

  const steps = useMemo(() => {
    return [
      <View style={styles.stepContainer}>
        <View style={[styles.stepHeader, { justifyContent: 'center', alignItems: 'center' }]}>
          <Text style={styles.greeting}>{`Oi, ${user.name}!`}</Text>
          <Text style={styles.question}>Fala pra mim se está curtindo o app?</Text>
        </View>
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.reactionButton, { backgroundColor: '#C33025' }]}
            onPress={dislikeApp}>
            <DisLikeIcon />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.reactionButton, { backgroundColor: '#009262' }]}
            onPress={openStore}>
            <LikeIcon />
          </TouchableOpacity>
        </View>
        <TouchableOpacity style={styles.laterButton} onPress={handleAvoidModal}>
          <Text style={styles.laterButtonText}>Avaliar mais tarde</Text>
        </TouchableOpacity>
      </View>,
      <ScrollView showsHorizontalScrollIndicator={false} showsVerticalScrollIndicator={false}>
        <View style={[styles.stepHeader, { justifyContent: 'center', alignItems: 'center' }]}>
          <Text style={[styles.problemTitle, { textAlign: 'center' }]}>
            Identificou algum problema?
          </Text>
          <Text style={[styles.problemSubtitle, { textAlign: 'center', fontWeight: 'bold' }]}>
            Das opções abaixo, diga o que aconteceu:
          </Text>
        </View>
        <View style={styles.checkboxGroup}>
          {TOPIC_OPTIONS.map((item, index) => (
            <React.Fragment key={index}>
              <TouchableOpacity
                activeOpacity={1}
                style={styles.checkboxRow}
                onPress={() => {
                  setSelectedTopics((prev) =>
                    prev.includes(item) ? prev.filter((t) => t !== item) : [...prev, item]
                  );
                }}>
                <View
                  style={[
                    styles.checkboxIndicator,
                    selectedTopics.includes(item) && styles.checkboxChecked,
                  ]}
                />
                <Text style={styles.checkboxLabel}>{item}</Text>
              </TouchableOpacity>
              {item === 'Outro problema. Qual?' && selectedTopics.includes(item) && (
                <TextInput
                  style={styles.textareaInput}
                  placeholder="Deixe seu comentário. (Opcional)"
                  placeholderTextColor="#ffffff"
                  value={_message}
                  onChangeText={_setMessage}
                  multiline
                />
              )}
            </React.Fragment>
          ))}
        </View>
        <View style={styles.sliderSection}>
          <Text style={styles.sliderLabel}>Gostaria deixar sua nota ao app?</Text>
          <View
            style={{ flexDirection: 'row', gap: 4, marginVertical: 8, justifyContent: 'center' }}>
            {Array.from({ length: sliderValue }).map((_, i) => (
              <Icons.CircleStar key={i} color="#FFD700" height="35" width="35" />
            ))}
          </View>
          <Slider
            value={sliderValue}
            onValueChange={setSliderValue}
            minimumValue={1}
            maximumValue={5}
            step={1}
            minimumTrackTintColor="#2240FF"
            maximumTrackTintColor="#ffffff"
            thumbTintColor="#2240FF"
            style={styles.sliderContainer}
          />
        </View>
        <View style={styles.actionButtons}>
          <TouchableOpacity style={styles.submitButton} onPress={handleFormSubmit}>
            <Text style={styles.submitButtonText}>Enviar</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cancelButton} onPress={handleCloseModal}>
            <Text style={styles.cancelButtonText}>Cancelar</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>,
      <View style={styles.stepContainer}>
        <View style={styles.stepHeader}>
          <Text style={styles.thankYouTitle}>Obrigado!</Text>
          <Text style={styles.thankYouSubtitle}>
            Sua opinião é fundamental{'\n'} para melhorarmos o app.{'\n'} Até a próxima!
          </Text>
        </View>
        <TouchableOpacity onPress={handleFinishForm}>
          <LinearGradient
            colors={['#6DFFBC', '#00AD5C']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 0.9 }}
            style={styles.closeButton}>
            <Text style={styles.closeButtonText}>Fechar</Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>,
    ];
  }, [user, sliderValue, _message, selectedTopics]);

  return (
    <BaseModal
      onClose={handleCloseModal}
      isOpen={rating.shouldItOpen}
      size={steps.indexOf(steps[currentStep]) === 1 ? 'md' : 'sm'}
      modalBody={steps[currentStep]}
      modalHeader={
        <View style={styles.modalHeader}>
          <Image style={styles.logo} source={BandHeaderLogo} resizeMode="contain" />
        </View>
      }
    />
  );
}

const styles = StyleSheet.create({
  stepContainer: {
    flex: 1,
    justifyContent: 'space-between',
    padding: 20,
  },
  stepHeader: {
    marginBottom: 20,
  },
  greeting: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'white',
    fontFamily: 'system-ui',
  },
  question: {
    fontSize: 18,
    color: 'white',
    textAlign: 'center',
    fontFamily: 'system-ui',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  reactionButton: {
    width: 80,
    height: 80,
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
  },

  laterButton: {
    width: '100%',
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  laterButtonText: {
    fontSize: 16,
    color: '#7A8CFF',
    fontFamily: 'system-ui',
  },
  scrollViewContent: {
    padding: 20,
  },
  problemTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'white',
    fontFamily: 'system-ui',
  },
  problemSubtitle: {
    fontSize: 18,
    marginBottom: 20,
    fontFamily: 'system-ui',
    color: '#fff',
  },
  checkboxGroup: {
    marginBottom: 20,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  checkboxIndicator: {
    width: 38,
    height: 38,
    borderWidth: 1,
    borderColor: '#182DB3',
    marginRight: 10,
    backgroundColor: 'transparent',
  },
  checkboxChecked: {
    backgroundColor: '#7A8CFF',
  },
  checkboxLabel: {
    fontSize: 16,
    color: '#ffffff',
    fontFamily: 'system-ui',
    fontWeight: 'bold',
  },
  textareaWrapper: {
    marginBottom: 20,
  },
  textareaLabel: {
    fontSize: 16,
    color: '#ffffff',
    marginBottom: 10,
    fontFamily: 'system-ui',
  },
  textareaBox: {
    width: '100%',
    height: 100,
    borderRadius: 10,
    borderColor: '#ccc',
    borderWidth: 1,
    padding: 10,
  },
  textareaInput: {
    fontSize: 16,
    color: '#ffffff',
    fontFamily: 'system-ui',
    borderWidth: 1,
    borderColor: '#182DB3',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginTop: 20,
    backgroundColor: '#070D31',
  },
  sliderSection: {
    marginBottom: 20,
  },
  sliderLabel: {
    fontSize: 16,
    color: '#ffffff',
    fontFamily: 'system-ui',
    textAlign: 'center',
  },
  sliderPlaceholder: {
    width: '100%',
    height: 20,
    borderRadius: 10,
    backgroundColor: '#ddd',
  },
  sliderValue: {
    fontSize: 18,
    marginVertical: 10,
    color: '#7A8CFF',
    textAlign: 'center',
    fontFamily: 'system-ui',
  },
  actionButtons: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
  },
  submitButton: {
    width: '100%',
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#182DB3',
  },
  submitButtonText: {
    fontSize: 16,
    color: '#fff',
    fontFamily: 'system-ui',
  },
  cancelButton: {
    width: '100%',
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#7A8CFF',
    fontFamily: 'system-ui',
  },
  thankYouTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    lineHeight: 32,
    color: '#ddd',
    textAlign: 'center',
    fontFamily: 'system-ui',
  },
  thankYouSubtitle: {
    fontSize: 18,
    color: '#ddd',
    marginBottom: 20,
    lineHeight: 22,
    textAlign: 'center',
    fontWeight: 'bold',
    fontFamily: 'system-ui',
  },
  closeButton: {
    width: '100%',
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#00AD5C',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.4,
    shadowRadius: 12,
    elevation: 8,
    marginVertical: 12,
  },
  closeButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#070D31',
    textTransform: 'capitalize',
    letterSpacing: 0.5,
    lineHeight: 20,
    fontFamily: 'system-ui',
  },
  modalHeader: {
    width: '100%',
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: 40,
    height: 40,
  },
  sliderContainer: {
    width: '100%',
  },
});
